import React, { useState, useRef } from "react";
import { Edit2Icon } from "lucide-react";
import { Input } from "../../ui/input";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from "../../ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import Spinner from "../../ui/spinner";
import { CompanyStage, companyStageNames } from "tekkr-common/dist/enums";
import { useUpdateOrgMetaMutation } from "../../../data/mutations/update-org-meta";

interface EditableNumberFieldProps {
    label: string;
    value: number | undefined;
    field: "techOrgSize" | "companySize";
    validate?: (value: number) => string | null;
}

function getValidationError(input: string, customValidate?: (n: number) => string | null): string | null {
    const num = parseInt(input, 10);
    if (isNaN(num)) return "Please enter a number";
    if (!Number.isInteger(num) || num < 1) return "Please enter a positive number";
    return customValidate?.(num) ?? null;
}

export function EditableNumberField({ label, value, field, validate }: EditableNumberFieldProps) {
    const [inputValue, setInputValue] = useState<string>(value?.toString() ?? "");
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const updateMutation = useUpdateOrgMetaMutation({});

    const validationError = isFocused ? getValidationError(inputValue, validate) : null;
    const errorMessage = validationError ?? (updateMutation.isError ? "Failed to save" : null);

    function handleBlur() {
        setIsFocused(false);
        const error = getValidationError(inputValue, validate);
        if (error) {
            setInputValue(value?.toString() ?? "");
            return;
        }
        const numValue = parseInt(inputValue, 10);
        if (numValue !== value) {
            updateMutation.mutate({ update: { [field]: numValue } });
        }
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (e.key === "Enter") {
            e.preventDefault();
            inputRef.current?.blur();
        } else if (e.key === "Escape") {
            setInputValue(value?.toString() ?? "");
            updateMutation.reset();
            inputRef.current?.blur();
        }
    }

    function handleFocus() {
        setIsFocused(true);
        setInputValue(value?.toString() ?? "");
    }

    return (
        <div className={"group flex flex-col"}>
            <span className={"text-xs font-semibold text-muted-foreground"}>{label}</span>
            <div className={"mt-1 flex flex-row items-center gap-2"}>
                {updateMutation.isPending ? (
                    <Spinner className={"h-4 w-4"} />
                ) : (
                    <Tooltip open={!!errorMessage}>
                        <TooltipTrigger asChild>
                            <div className={"flex flex-row items-center gap-2"}>
                                <Input
                                    ref={inputRef}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={isFocused ? inputValue : (value?.toString() ?? "")}
                                    onChange={(e) => {
                                        setInputValue(e.target.value);
                                        updateMutation.reset();
                                    }}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onKeyDown={handleKeyDown}
                                    className={
                                        "h-auto w-12 border-none bg-transparent p-0 font-medium shadow-none " +
                                        "focus-visible:bg-input focus-visible:px-2 focus-visible:ring-1"
                                    }
                                    placeholder="—"
                                />
                                {!isFocused && (
                                    <Edit2Icon
                                        className={"h-3 w-3 cursor-pointer text-muted-foreground"}
                                        onClick={() => inputRef.current?.focus()}
                                    />
                                )}
                            </div>
                        </TooltipTrigger>
                        <TooltipContent
                            side="bottom"
                            className={
                                updateMutation.isError
                                    ? "bg-destructive text-destructive-foreground"
                                    : "bg-muted text-foreground"
                            }
                        >
                            {errorMessage}
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}

interface EditableStageFieldProps {
    value: CompanyStage | undefined;
}

export function EditableStageField({ value }: EditableStageFieldProps) {
    const updateMutation = useUpdateOrgMetaMutation({});

    function handleChange(newValue: CompanyStage) {
        if (newValue !== value) {
            updateMutation.mutate({ update: { stage: newValue } });
        }
    }

    const errorMessage = updateMutation.isError ? "Failed to save" : null;

    return (
        <div className={"group flex flex-col"}>
            <span className={"text-xs font-semibold text-muted-foreground"}>Funding Stage</span>
            <div className={"mt-1 flex flex-row items-center gap-2"}>
                {updateMutation.isPending ? (
                    <Spinner className={"h-4 w-4"} />
                ) : (
                    <Tooltip open={!!errorMessage}>
                        <TooltipTrigger asChild>
                            <div>
                                <Select value={value} onValueChange={(v) => handleChange(v as CompanyStage)}>
                                    <SelectTrigger
                                        className={
                                            "h-auto w-auto gap-2 border-none bg-transparent p-0 font-medium shadow-none focus:ring-0 " +
                                            "[&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
                                        }
                                    >
                                        <SelectValue placeholder={<span className={"opacity-35"}>&mdash;</span>} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectLabel>Funding Stage</SelectLabel>
                                            {Object.values(CompanyStage).map((stage) => (
                                                <SelectItem key={stage} value={stage}>
                                                    {companyStageNames[stage]}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-destructive text-destructive-foreground">
                            {errorMessage}
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}
